import { getApiUrl } from "@/utils/api";
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Search, X, SlidersHorizontal, Bell } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import axios from "axios";

const API = getApiUrl();

export const PropertyFilters = ({ onFilterChange, initialFilters = {}, inline = false }) => {
  const [locations, setLocations] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    location: initialFilters.location || "",
    minPrice: initialFilters.minPrice || "",
    maxPrice: initialFilters.maxPrice || "",
    beds: initialFilters.beds || "",
  });

  useEffect(() => { fetchLocations(); }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/properties/locations`);
      setLocations(response.data.locations || []);
    } catch (error) {
      console.error("Failed to fetch locations:", error);
    }
  };

  const handleChange = (name, value) => {
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);
    if (inline || showFilters) {
      const cleanFilters = {};
      const loc = name === "location" ? value : filters.location;
      if (loc && loc !== "all") cleanFilters.display_address = loc;
      const minP = name === "minPrice" ? value : filters.minPrice;
      const maxP = name === "maxPrice" ? value : filters.maxPrice;
      const beds = name === "beds" ? value : filters.beds;
      if (minP) cleanFilters.min_price = parseInt(minP);
      if (maxP) cleanFilters.max_price = parseInt(maxP);
      if (beds && beds !== "any") cleanFilters.beds = parseInt(beds);
      onFilterChange(cleanFilters);
    }
  };

  const handleClear = () => {
    setFilters({ location: "", minPrice: "", maxPrice: "", beds: "" });
    onFilterChange({});
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== "");

  if (inline) {
    return (
      <div data-testid="property-filters-inline">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          className="rounded-none text-xs h-9"
          data-testid="toggle-filters-btn"
        >
          <SlidersHorizontal size={14} className="mr-1.5" />
          Filters
          {hasActiveFilters && <span className="ml-1.5 w-2 h-2 rounded-full bg-primary" />}
        </Button>

        {showFilters && (
          <div className="flex flex-wrap items-center gap-3 mt-3 animate-fade-in-up">
            <Select value={filters.location} onValueChange={(v) => handleChange("location", v === "all" ? "" : v)}>
              <SelectTrigger className="w-[140px] h-9 rounded-none text-sm"><SelectValue placeholder="Location" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map((loc) => <SelectItem key={loc} value={loc}>{loc}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={filters.beds} onValueChange={(v) => handleChange("beds", v === "any" ? "" : v)}>
              <SelectTrigger className="w-[100px] h-9 rounded-none text-sm"><SelectValue placeholder="Beds" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any Beds</SelectItem>
                <SelectItem value="1">1+ Beds</SelectItem>
                <SelectItem value="2">2+ Beds</SelectItem>
                <SelectItem value="3">3+ Beds</SelectItem>
                <SelectItem value="4">4+ Beds</SelectItem>
              </SelectContent>
            </Select>

            <Input type="number" placeholder="Min Price" value={filters.minPrice} onChange={(e) => handleChange("minPrice", e.target.value)} className="w-[100px] h-9 rounded-none text-sm" />
            <Input type="number" placeholder="Max Price" value={filters.maxPrice} onChange={(e) => handleChange("maxPrice", e.target.value)} className="w-[100px] h-9 rounded-none text-sm" />

            {hasActiveFilters && (
              <>
                <Button variant="ghost" size="sm" onClick={handleClear} className="h-9 text-muted-foreground hover:text-foreground">
                  <X size={14} className="mr-1" />Clear
                </Button>
                {/* Turn the current filter combo into a saved alert. Prefills
                    the "New alert" dialog on /my-alerts via URL params so the
                    visitor doesn't have to re-type anything. */}
                <Link
                  to={`/my-alerts?new=1${filters.location ? `&postcode=${encodeURIComponent(filters.location)}` : ""}${filters.beds ? `&min_beds=${filters.beds}` : ""}${filters.minPrice ? `&min_price=${filters.minPrice}` : ""}${filters.maxPrice ? `&max_price=${filters.maxPrice}` : ""}`}
                  data-testid="save-search-as-alert-btn"
                >
                  <Button variant="outline" size="sm" className="h-9 rounded-none">
                    <Bell size={14} className="mr-1" />Save as alert
                  </Button>
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default PropertyFilters;
